/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { HtmlHeadInclude } from '@msdyn365-commerce/core';
import * as React from 'react';
import { IHeiSailthruSignupFormViewProps } from './hei-sailthru-signup-form';

let sailthruInit = false;
const validateEmail = (email: string) => {
    return email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

export default (props: IHeiSailthruSignupFormViewProps) => {
    const locale = props.context.request.locale ?? 'en-CA';
    const { customerId, template, source, creatorTypeOptions, className } = props.config;
    const [email, setEmail] = React.useState('');
    const [creatorType, setCreatorType] = React.useState('');
    const [isSubmited, setIsSubmited] = React.useState(false);
    const [emailError, setEmailError] = React.useState(false);
    const [creatorTypeError, setCreatorTypeError] = React.useState(false);

    React.useEffect(() => {
        if (!sailthruInit && window['Sailthru']) {
            sailthruInit = true;
            const Sailthru = window['Sailthru'];
            Sailthru.init({ customerId: customerId });
        }
    }, [customerId]);

    const options = React.useMemo(() => {
        const optionText = creatorTypeOptions;

        if (!optionText) {
            return [];
        }

        return optionText
            .split(';')
            .filter(s => !!s)
            .map(s => {
                const opt = s.split(':');
                return {
                    val: opt[0],
                    label: opt[1]
                };
            });
    }, [creatorTypeOptions]);

    const showCreatorType = options.length > 0;
    let wrapperCls = 'hei-sailthru-signup-form';
    if (className) {
        wrapperCls += ` ${className}`;
    }

    if (showCreatorType) {
        wrapperCls += ` hei-sailthru-vertical`;
    }

    return (
        <div className={`row ${wrapperCls}`}>
            <HtmlHeadInclude>
                <script data-load-point='headEnd' src='https://ak.sail-horizon.com/spm/spm.v1.min.js' async></script>
            </HtmlHeadInclude>

            {isSubmited ? (
                <div className='hei-sailthru-signup-form-message'>{props.resources.postNewsletterSubscribeMsg}</div>
            ) : (
                <>
                    <div className='hei-sailthru-signup-form-email form-group'>
                        {emailError && <div className='invalid-feedback'>{props.resources.emailErrorMessage}</div>}
                        <input
                            className='form-control'
                            type='email'
                            placeholder={props.resources.emailInputPlaceholder}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    {showCreatorType && (
                        <div className='hei-sailthru-signup-form-creator-type form-group'>
                            {creatorTypeError && <div className='invalid-feedback'>{props.resources.creatorTypeErrorMessage}</div>}
                            <select onChange={e => setCreatorType(e.target.value)} className='form-control'>
                                <option value=''>{props.resources.creatorTypeInputPlaceholder}</option>
                                {options.map(o => (
                                    <option key={o.val} selected={creatorType === o.val} value={o.val}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className='hei-sailthru-signup-form-btn'>
                        <button
                            className='btn msc-btn'
                            type='button'
                            onClick={() => {
                                const Sailthru = window['Sailthru'];
                                if (!Sailthru) {
                                    return;
                                }

                                setCreatorTypeError(false);
                                setEmailError(false);

                                var hasError = false;
                                if (!email || !validateEmail(email)) {
                                    setEmailError(true);
                                    hasError = true;
                                }

                                if (showCreatorType && !creatorType) {
                                    setCreatorTypeError(true);
                                    hasError = true;
                                }

                                if (hasError) {
                                    return;
                                }

                                const varsPayload = showCreatorType
                                    ? {
                                          creator_type: creatorType,
                                          language: locale
                                      }
                                    : {
                                          language: locale
                                      };

                                const payload = {
                                    id: email,
                                    key: 'email',
                                    template: { name: template },
                                    source: source,
                                    vars: varsPayload,
                                    onSuccess: () => {
                                        setIsSubmited(true);
                                    },
                                    onError: () => {
                                        console.warn('Sailthru subscription failed.');
                                    }
                                };

                                Sailthru.integration('userSignUpConfirmedOptIn', payload);
                            }}
                        >
                            {props.resources.submitBtnLabel}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};
